import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { MINUTES } from '@constants/miscellaneous';
import { IDWAssetUserInfo, IDWUserReports, IDWReferentialQuote, IQuoteServiceResponse } from '@interfaces/drivewealth';
import { ErrorAndLoadingService } from '@services/error-loading/error-loading.service';
import { FirebaseService } from '../firebase/firebase.service';

@Injectable({
  providedIn: 'root',
})
export class DrivewealthService {

  drivewealthAccountSummary: {[key: string]: IDWAssetUserInfo};

  accountSummarySource = new BehaviorSubject<{[key: string]: IDWAssetUserInfo}>(null);
  currentAccountSummary = this.accountSummarySource.asObservable();
  private drivewealthAccountRestricted = new BehaviorSubject<boolean>(false);
  currentAccountRestricted = this.drivewealthAccountRestricted.asObservable();
  private drivewealthCashAvailableForTrade = new BehaviorSubject<number>(0);
  currentDrivewealthCashAvailableForTrade = this.drivewealthCashAvailableForTrade.asObservable();
  private cashAvailableForWithdrawalSource = new BehaviorSubject<number>(null);
  currentCashAvailableForWithdrawal = this.cashAvailableForWithdrawalSource.asObservable();
  private drivewealthCashSettling = new BehaviorSubject<number>(0);
  currentDrivewealthCashSettling = this.drivewealthCashSettling.asObservable();
  private drivewealthCashBalance = new BehaviorSubject<number>(0);
  currentDrivewealthCashBalance = this.drivewealthCashBalance.asObservable();
  private getDrivewealthAccountSummaryCoolDown: Date | null = null;

  constructor (
    private errorService: ErrorAndLoadingService,
    private firebaseService: FirebaseService,
  ) {
    this.drivewealthAccountSummary = {};
  }

  public async drivewealthStatementsGet(): Promise<IDWUserReports> {
    const drivewealthStatementsGetFn = this.firebaseService.functions.httpsCallable('DrivewealthStatementsGet');
    const data = (await drivewealthStatementsGetFn({})).data as IDWUserReports;
    if (!data) { return null; }
    if (data.statements) { data.statements.reverse(); }
    if (data.confirms) { data.confirms.reverse(); }
    if (data.taxForms) { data.taxForms.reverse(); }
    return data;
  }

  public async drivewealthStatementGetURL(fileKey: string): Promise<string> {
    const drivewealthStatementGetURLFn = this.firebaseService.functions.httpsCallable('DrivewealthStatementGetURL');
    const result = await drivewealthStatementGetURLFn({fileKey});
    return (result.data && result.data.response.url) || '';
  }

  public async drivewealthGetReferentialQuotes(
    symbols: string[],
    amount: number,
    movementType: ('BUY' | 'SELL'),
    shares: number,
  ): Promise<IQuoteServiceResponse> {
    const drivewealthGetReferentialQuotesFn = this.firebaseService.functions.httpsCallable('DrivewealthGetReferentialQuotes');
    const result = await drivewealthGetReferentialQuotesFn({ symbols, amount, movementType, shares });
    const response = {
      quoteId: result.data.quoteId,
      USD_CLP: result.data.USD_CLP,
      USD_CLP_SELL: result.data.USD_CLP_SELL,
      commission: result.data.commission,
      assetPrices: {},
    };
    (result.data.referentialQuotes as IDWReferentialQuote[]).forEach(a => response.assetPrices[a.symbol] = a.ask);
    return response;
  }

  public async getDrivewealthAccountSummary() {
    const now = new Date();
    if (this.getDrivewealthAccountSummaryCoolDown && now < this.getDrivewealthAccountSummaryCoolDown) {
      return;
    }
    try {
      const drivewealthAccountSummaryFn = this.firebaseService.functions.httpsCallable('DrivewealthAccountSummary');
      const response = await drivewealthAccountSummaryFn({});
      if (!response || !response.data || !response.data.response || !response.data.response.accountSummary) {
        const toastErrorText = 'Hubo un error al obtener los datos de Stocks. Reintenta más tarde.';
        this.errorService.presentToastWithDuration(2000, 'top', toastErrorText, false);
        throw new Error('Error getting drivewealth account summary');
      }
      const accountSummary = response.data.response.accountSummary;
      const newAccountSummary = {};
      accountSummary.equity.equityPositions.forEach(asset => {
        newAccountSummary[asset.symbol] = {
          shares: asset.openQty,
          amount: asset.marketValue,
          costBasis: asset.costBasis,
          return: ((asset.marketValue / asset.costBasis) - 1) * 100,
          profit: asset.unrealizedPL,
          dayReturn: asset.unrealizedDayPLPercent,
          dayProfit: asset.unrealizedDayPL,
          lastUpdated: accountSummary.lastUpdated,
        };
      });
      this.drivewealthAccountSummary = newAccountSummary;
      this.accountSummarySource.next(this.drivewealthAccountSummary);
      this.drivewealthAccountRestricted.next(accountSummary.restricted);
      this.drivewealthCashAvailableForTrade.next(accountSummary.cash.cashAvailableForTrade);
      this.cashAvailableForWithdrawalSource.next(accountSummary.cash.cashAvailableForWithdrawal);
      this.drivewealthCashBalance.next(accountSummary.cash.cashBalance);
      let cashSettlementsSum = 0;
      const cashSettlements = accountSummary.cash.cashSettlement as { utcTime: string, cash: number }[];
      cashSettlements.forEach(cashSettlement => cashSettlementsSum += cashSettlement.cash);
      this.drivewealthCashSettling.next(cashSettlementsSum);
      this.getDrivewealthAccountSummaryCoolDown = new Date(now.getTime() + MINUTES);
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Hubo un error al obtener los datos de Stocks. Reintenta más tarde.', false);
      throw new Error('Error getting drivewealth account summary');
    }
  }

  public deleteLocalDrivewealthAccountSummary() {
    this.drivewealthAccountSummary = {};
    this.accountSummarySource.next(null);
    this.drivewealthAccountRestricted.next(false);
    this.drivewealthCashAvailableForTrade.next(null);
    this.drivewealthCashBalance.next(0);
    this.cashAvailableForWithdrawalSource.next(null);
    this.drivewealthCashSettling.next(0);
  }
}
